import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import Badge from "../../CommonComponents/Badge";
import Search from "../../CommonComponents/Search";
import Table from "../../CommonComponents/Table";
import Button from "../../Button";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import { libTypeIds, RESULT_STATUS } from "../../../constants";
import { useDebouncedValue } from "../../../Hooks/useDebounceValue";
import Toast from "../../CommonComponents/Toast";
import { Slide } from "@mui/material";
import Loading from "../../Notification-Loading/Loading/Loading";
import useAuth from "../../../Hooks/useAuth";

const QuestionList = ({
  setShowQuestionList,
  topicId,
  complexityId,
  languages,
  type = libTypeIds.MCQ,
  neededCount = 0,
  onSave = null,
  fixedQnIds = [],
  isPublished = false,
}) => {
  const axios = useAxiosPrivate();
  const { isLoaded, setIsLoaded, setShowNotify } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedQns, setSelectedQns] = useState(
    fixedQnIds?.length ? fixedQnIds ?? [] : []
  );
  const [searchValue, setSearchValue] = useState("");
  const [error, setError] = useState("");
  const [openToast, setOpenToast] = useState(false);

  const debouncedSearchValue = useDebouncedValue(searchValue, 500);

  const onClick = () => {
    if (neededCount < selectedQns.length) {
      setError("You have selected more than configured count.");
      setOpenToast(true);
      return;
    } else if (neededCount > selectedQns.length) {
      setError("You have selected less than configured count.");
      setOpenToast(true);
      return;
    }
    onSave && onSave(selectedQns);
    setShowQuestionList(false);
  };

  useEffect(() => {
    const getQuestions = async () => {
      setIsLoaded(true);
      try {
        const { data } = await axios.post(
          "node/admin/question/getbytopicandcomplexity",
          {
            topicid: topicId,
            languages: languages ?? [],
            complexityid: complexityId,
            libtypeid: type,
            fixedQnIds,
          }
        );
        if (data.resultCode === RESULT_STATUS.SUCCESS) {
          setQuestions(data.data ?? []);
          setFilteredQuestions(data.data ?? []);
        } else {
          setShowNotify({
            show: true,
            msg:
              data.data?.msg ??
              "Unable to get question list. Please try again later",
            title: "Error !",
          });
        }
      } catch (error) {
        let isUnAuth = error.message.includes("403");
        setShowNotify({
          isUnAuth,
          title: isUnAuth ? "Warning !" : "Error !",
          msg: isUnAuth
            ? "You have been logged-out due to inactivity. Login again."
            : "Unable to get test details. Please try again later",
          show: true,
        });
      } finally {
        setIsLoaded(false);
      }
    };
    getQuestions();
  }, [
    axios,
    topicId,
    complexityId,
    languages,
    type,
    setIsLoaded,
    setShowNotify,
  ]);

  useEffect(() => {
    let questionList = structuredClone(questions);
    questionList = questionList.filter((qn) =>
      qn.name?.toLowerCase()?.includes(debouncedSearchValue?.toLowerCase())
    );
    setFilteredQuestions(questionList);
  }, [debouncedSearchValue, questions]);

  return (
    <div className="test-question-list-container">
      <Toast
        open={openToast}
        type="warning"
        onClose={() => setOpenToast(false)}
        message={error}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        slots={{ transition: Slide }}
      />
      <div className="test-question-list-header-container">
        <div className="test-question-list-header-left-container">
          <h2>Question List</h2>
          <Badge
            size="sm"
            color="primary"
            label={`${questions.length ?? 0} question${
              questions.length > 1 ? "s" : ""
            }`}
          />
        </div>
        <div className="test-question-list-header-right-container">
          <p className="test-question-list-total-selected-count">
            Selected Questions : {selectedQns?.length ?? 0}
          </p>
          <Search
            placeholder="Search"
            iconPosition={"end"}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
      </div>
      <Table
        selectable={true}
        fixedHeader
        columns={[
          {
            key: "name",
            title: "Question",
            render: (col) => (
              <ReactQuill
                className="test-question-list-name"
                readOnly={true}
                value={col}
              />
            ),
          },
          {
            key: "topicname",
            title: "Topic",
          },
          {
            key: "libname",
            title: "Type",
          },
          {
            key: "complexityname",
            title: "Level",
            render: (col) => (
              <div className={`complexity-${col?.toLowerCase()}`}>{col}</div>
            ),
          },
        ]}
        className="test-question-list-table"
        data={filteredQuestions}
        onRowSelect={(row) => setSelectedQns(row)}
        selectedRows={selectedQns}
      />
      {!isPublished ? (
        <div className="test-question-list-btn-container">
          <Button
            size="sm"
            hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
            onClick={() => setShowQuestionList(false)}
          />
          <Button
            size="sm"
            hierarchy={{ type: "primary", buttonText: "Save" }}
            onClick={onClick}
            disable={selectedQns.length === 0}
          />
        </div>
      ) : null}
      {isLoaded && <Loading />}
    </div>
  );
};

export default QuestionList;
